/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import routes, { enrollRoutes } from '../../consts/routes'
import Info from './Info'
import Qualification from './Qualification'
import $t from '../../i18n'
import SaveProgressDialog from '../dialogs/SaveProgressDialog'
import PlanRank from './PlanRank'
import Plans from './Plans'
import Details from './Details'
import Finalize from './Finalize/Finalize'
import PlanExtended from './Plans/PlanExtended'
import Success from './Success'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../actions'
import { shouldOpenSaveProgressDialog } from 'utils/generalUtils'
import RejectEnrollmentPage from 'components/common/RejectEnrollmentPage'
import { REJECT_ENROLLMENT_REASONS } from 'consts/flowConsts'
import { useIdleTimer } from 'react-idle-timer'
import { IDLE_TIMEOUT, SUPPORT_PHONE_NUMBER } from 'consts'
import TimeoutModal from 'components/common/TimeoutModal'
import ShareProgressDialog from 'components/dialogs/ShareProgressDialog'
import ShareQuoteDialog from 'components/dialogs/ShareQuoteDialog'
import { LAYOUT_TYPE, DIALOG_TYPE } from 'consts'

import './Enroll.css'
import ComparePlans from './Plans/ComparePlans'

const Enroll = ({ isSavePlanShouldAppear }) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const [isMouseNearTop, setIsMouseNearTop] = useState(false)

  const saveProgressDialogTimeToOpen = useSelector(
    state => state.app?.saveProgressDialogTimeToOpen
  )

  const isBroker = useSelector(state => state.app.layout === LAYOUT_TYPE.broker)

  const isStepsVisible = location.pathname !== routes.ENROLL_NO_ELIGIBLE
  const isNoElligibleToEnroll = location.pathname === routes.ENROLL_NO_ELIGIBLE

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
    }
  }, [])

  useEffect(() => {
    if (
      isMouseNearTop &&
      shouldOpenSaveProgressDialog(saveProgressDialogTimeToOpen)
    ) {
      dispatch(
        Actions.createAction(Actions.SAVE_DIALOG_OPEN_CLICKED, {
          type: DIALOG_TYPE.SAVE_PROGRESS
        })
      )
    }
    setIsMouseNearTop(false)
  }, [isMouseNearTop])

  useEffect(() => {
    // on react router v6 the scroll is preserve between pages
    // this is a workaround to scroll to top when navigating between pages
    // we need to scroll to top only in case there is no hash on the location
    // the hash is used for navigate to specific section on the page
    if (!location.hash) {
      document.getElementById('EnrollFlow')?.scrollIntoView()
    }
  }, [location?.pathname])

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: () => dispatch(Actions.createAction(Actions.USER_INTERACTED)),
    startOnMount: true,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove'
    ]
  })

  // open save progress dialog when mouse is near top of the screen
  // once per 30 minutes per sessions or 1 hour between sessions
  // the dialog can be opened only on the enrollment page after the qualification page
  const onMouseMove = e => {
    // we need to take the current location path from the window object
    // because the location object is not updated when the mouse event is triggered
    const currentLocationPath = window.location?.pathname
    const isSaveProgressAvailable =
      currentLocationPath.includes(routes.ENROLL) &&
      currentLocationPath !== routes.ENROLL_INFO &&
      currentLocationPath !== routes.ENROLL_QUALIFICATION &&
      currentLocationPath !== routes.ENROLL_NO_ELIGIBLE &&
      currentLocationPath !== routes.ENROLL_SUCCESS
    const positionFromTop = e.y
    if (isSaveProgressAvailable && positionFromTop <= 0) {
      setIsMouseNearTop(true)
    }
  }

  const enrollSteps = [
    {
      title: $t('enroll.steps.info'),
      path: routes.ENROLL_INFO,
      selected: true
    },
    {
      title: $t('enroll.steps.plans'),
      path: routes.ENROLL_PLAN_RANK,
      selected:
        location.pathname !== routes.ENROLL_INFO &&
        location.pathname !== routes.ENROLL_QUALIFICATION
    },
    {
      title: $t('enroll.steps.enrollment'),
      path: routes.ENROLL_MEMBER_DETAILS,
      selected:
        location.pathname !== routes.ENROLL_INFO &&
        location.pathname !== routes.ENROLL_QUALIFICATION &&
        location.pathname !== routes.ENROLL_PLAN_RANK &&
        !location.pathname.includes(routes.ENROLL_PLANS)
    },
    {
      title: $t('enroll.steps.finalize'),
      path: routes.ENROLL_FINALIZE,
      selected:
        location.pathname !== routes.ENROLL_INFO &&
        location.pathname !== routes.ENROLL_QUALIFICATION &&
        location.pathname !== routes.ENROLL_PLAN_RANK &&
        !location.pathname.includes(routes.ENROLL_PLANS) &&
        !location.pathname.includes(routes.ENROLL_MEMBER_DETAILS)
    }
  ]

  const Steps = () => {
    const dispatch = useDispatch()
    return (
      <div className='enroll-steps'>
        {enrollSteps.map((step, index) => {
          return (
            <div
              key={index}
              className='step'
              data-selected={step?.selected}
              onClick={() => {
                if (step.selected && step.path !== location.pathname)
                  dispatch(
                    Actions.createAction(Actions.NAVIGATED_TO, step.path)
                  )
              }}
            >
              <div className='step-number' />
              <span className='step-title'>{step?.title}</span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div
      className={
        isNoElligibleToEnroll ? 'enroll-page reject-page' : 'enroll-page'
      }
      id='EnrollFlow'
    >
      {!isNoElligibleToEnroll && (
        <div className='top-banner'>
          <div className='top-banner-content'>
            <span className='bold'>{$t('home.footer')} </span>
            {$t('common.call', { supportPhone: SUPPORT_PHONE_NUMBER })}
          </div>
        </div>
      )}
      <div className='enroll-body'>
        {isStepsVisible && <Steps />}
        <Routes>
          <Route path={enrollRoutes.INFO} element={<Info />} />
          <Route
            path={enrollRoutes.QUALIFICATION}
            element={<Qualification />}
          />
          <Route
            path={enrollRoutes.NO_ELIGIBLE}
            element={
              <RejectEnrollmentPage
                reason={REJECT_ENROLLMENT_REASONS.NON_ELIGIBLE}
              />
            }
          />
          <Route path={enrollRoutes.PLAN_RANK} element={<PlanRank />} />
          <Route path={enrollRoutes.PLANS} element={<Plans />} />
          <Route path={enrollRoutes.PLAN} element={<PlanExtended />} />
          <Route path={enrollRoutes.PLANS_COMPARE} element={<ComparePlans />} />
          <Route path={enrollRoutes.MEMBER_DETAILS} element={<Details />} />
          <Route path={enrollRoutes.FINALIZE} element={<Finalize />} />
          <Route path={enrollRoutes.SUCCESS} element={<Success />} />
        </Routes>
      </div>
      {!isBroker && <SaveProgressDialog />}
      {isBroker && <ShareProgressDialog />}
      {isBroker && <ShareQuoteDialog />}
      <TimeoutModal isSavePlanShouldAppear={isSavePlanShouldAppear} />
    </div>
  )
}

export default Enroll
